import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Shield, Mail, Lock, Eye, EyeOff, AlertCircle, KeyRound, RefreshCw, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { LogoIcon } from '../components/Logo';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // 2FA State
  const [requires2FA, setRequires2FA] = useState(false);
  const [twofaSessionId, setTwofaSessionId] = useState('');
  const [twofaCode, setTwofaCode] = useState(['', '', '', '', '', '']);
  const [twofaError, setTwofaError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendsRemaining, setResendsRemaining] = useState(3);
  const [countdown, setCountdown] = useState(600); // 10 minutes in seconds
  
  const codeInputRefs = useRef([]);
  const { login, setAuthFromResponse } = useAuth();
  const navigate = useNavigate();

  // Countdown timer for 2FA expiration
  useEffect(() => {
    if (requires2FA && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    } else if (countdown === 0 && requires2FA) {
      setRequires2FA(false);
      setTwofaSessionId('');
      setTwofaCode(['', '', '', '', '', '']);
      toast.error('Verification code expired. Please login again.');
    }
  }, [requires2FA, countdown]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    
    try {
      const response = await axios.post(`${API}/api/auth/login`, { email, password });
      const data = response.data;
      
      // Check if 2FA is required
      if (data.requires_2fa) {
        setRequires2FA(true);
        setTwofaSessionId(data.twofa_session_id);
        setCountdown(data.expires_in || 600);
        toast.success('Verification code sent to your email');
        // Focus first input
        setTimeout(() => codeInputRefs.current[0]?.focus(), 100);
      } else {
        // Normal login success
        setAuthFromResponse(data);
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Login failed';
      const status = error.response?.status;
      
      if (status === 429) {
        setErrorMessage(detail);
        toast.error('Account temporarily locked');
      } else if (status === 403) {
        if (detail.includes('not verified')) {
          setErrorMessage(detail);
          toast.error('Please verify your email first');
        } else {
          setErrorMessage(detail);
          toast.error('Access denied');
        }
      } else {
        setErrorMessage(detail);
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newCode = [...twofaCode];
    newCode[index] = value;
    setTwofaCode(newCode);
    setTwofaError('');
    
    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    // Handle backspace - move to previous input
    if (e.key === 'Backspace' && !twofaCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setTwofaCode(newCode);
      codeInputRefs.current[5]?.focus();
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    const code = twofaCode.join('');
    
    if (code.length !== 6) {
      setTwofaError('Please enter the complete 6-digit code');
      return;
    }
    
    setLoading(true);
    setTwofaError('');
    
    try {
      const response = await axios.post(`${API}/api/auth/verify-2fa`, {
        session_id: twofaSessionId,
        code: code
      });
      
      const data = response.data;
      setAuthFromResponse(data);
      toast.success('Welcome back, Admin!');
      
      // Redirect super admin to admin panel
      if (data.user?.role === 'super_admin') {
        navigate('/cd-secure-7e511cdb');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Verification failed';
      setTwofaError(detail);
      
      if (detail.includes('expired') || detail.includes('login again')) {
        setRequires2FA(false);
        setTwofaSessionId('');
        setTwofaCode(['', '', '', '', '', '']);
      } else {
        // Clear code on error
        setTwofaCode(['', '', '', '', '', '']);
        codeInputRefs.current[0]?.focus();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendsRemaining <= 0) return;
    
    setResendLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/auth/resend-2fa`, {
        session_id: twofaSessionId
      });
      
      setResendsRemaining(response.data.resends_remaining);
      setCountdown(600); // Reset countdown
      setTwofaCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
      toast.success('New verification code sent!');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to resend code';
      toast.error(detail);
      
      if (detail.includes('login again')) {
        setRequires2FA(false);
        setTwofaSessionId('');
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setRequires2FA(false);
    setTwofaSessionId('');
    setTwofaCode(['', '', '', '', '', '']);
    setTwofaError('');
    setCountdown(600);
  };

  // 2FA Verification UI
  if (requires2FA) {
    return (
      <div className="min-h-screen bg-slate-950 flex" data-testid="2fa-page">
        <div className="flex-1 flex items-center justify-center p-8">
          <Card className="w-full max-w-md bg-slate-900 border-slate-800">
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-500/20 flex items-center justify-center">
                <KeyRound className="w-8 h-8 text-blue-500" />
              </div>
              <CardTitle className="text-2xl font-heading text-white">Two-Factor Authentication</CardTitle>
              <CardDescription className="text-slate-400">
                A verification code has been sent to your email address
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Timer */}
              <div className="mb-6 text-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 text-slate-400">
                  <span className="text-sm">Code expires in</span>
                  <span className={`font-mono font-bold ${countdown < 60 ? 'text-red-400' : 'text-white'}`}>
                    {formatTime(countdown)}
                  </span>
                </div>
              </div>

              {/* Error Alert */}
              {twofaError && (
                <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-400">{twofaError}</div>
                </div>
              )}
              
              <form onSubmit={handleVerify2FA} className="space-y-6">
                {/* 6-digit code input */}
                <div className="space-y-2">
                  <Label className="text-slate-300 text-center block">Enter verification code</Label>
                  <div className="flex justify-center gap-2" onPaste={handleCodePaste}>
                    {twofaCode.map((digit, index) => (
                      <Input
                        key={index}
                        ref={(el) => (codeInputRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(index, e)}
                        className="w-12 h-14 text-center text-2xl font-mono bg-slate-800 border-slate-700 text-white focus:border-blue-500 focus:ring-blue-500"
                        autoFocus={index === 0}
                      />
                    ))}
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={loading || twofaCode.join('').length !== 6}
                >
                  {loading ? 'Verifying...' : 'Verify & Sign In'}
                </Button>
              </form>

              {/* Resend Code */}
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendLoading || resendsRemaining <= 0}
                  className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 disabled:text-slate-600 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`w-4 h-4 ${resendLoading ? 'animate-spin' : ''}`} />
                  {resendLoading ? 'Sending...' : `Resend code (${resendsRemaining} left)`}
                </button>
              </div>

              {/* Back to login */}
              <div className="mt-6 pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="w-full flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-white"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to login
                </button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right - Security Info */}
        <div 
          className="hidden lg:flex flex-1 items-center justify-center p-12 bg-gradient-to-br from-slate-900 to-slate-950"
        >
          <div className="max-w-md text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-500/20 flex items-center justify-center">
              <Shield className="w-10 h-10 text-blue-500" />
            </div>
            <h2 className="font-heading font-bold text-2xl text-white mb-4">
              Enhanced Security
            </h2>
            <p className="text-slate-400 mb-6">
              Two-factor authentication adds an extra layer of security to your super admin account.
            </p>
            <div className="text-left space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-emerald-400 text-xs">✓</span>
                </div>
                <p className="text-sm text-slate-300">Protects against unauthorized access</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-emerald-400 text-xs">✓</span>
                </div>
                <p className="text-sm text-slate-300">Verification codes expire after 10 minutes</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-emerald-400 text-xs">✓</span>
                </div>
                <p className="text-sm text-slate-300">All login attempts are logged and monitored</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Normal Login UI
  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="login-page">
      {/* Left - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <Card className="w-full max-w-md bg-slate-900 border-slate-800">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <LogoIcon size="xl" />
            </div>
            <CardTitle className="text-2xl font-heading text-white">Welcome Back</CardTitle>
            <CardDescription className="text-slate-400">
              Sign in to your Clear Defense AI account
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Error Alert */}
            {errorMessage && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-400">
                  {errorMessage}
                  {errorMessage.includes('not verified') && (
                    <Link 
                      to={`/verify-email?email=${encodeURIComponent(email)}`}
                      className="block mt-2 text-blue-400 hover:text-blue-300 underline"
                    >
                      Resend verification email
                    </Link>
                  )}
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setErrorMessage(''); }}
                    className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-300">Password</Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-xs text-blue-400 hover:text-blue-300"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setErrorMessage(''); }}
                    className="pl-10 pr-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-slate-400">
                Don&apos;t have an account?{' '}
                <Link to="/signup" className="text-blue-500 hover:text-blue-400" data-testid="signup-link">
                  Create one
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Right - Hero Image */}
      <div 
        className="hidden lg:flex flex-1 items-center justify-center p-12"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1719702703328-87e335030324?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="glass rounded-2xl p-8 max-w-md text-center">
          <h2 className="font-heading font-bold text-3xl text-white mb-4">
            Multi-Layer Cybersecurity
          </h2>
          <p className="text-slate-300">
            Protect your organization with unified DNS, file, email, and application threat detection powered by AI.
          </p>
        </div>
      </div>
    </div>
  );
}
